import type { ProcessSSECallbacks } from '@modules/MessageParser/helper';
import type { ProjectDetail, ProjectSettings } from '@stores/project/types';
import type { WegicRequestConfig } from '@wegic/request';
import type { DownloadProjectSourceResponse } from './const';
import type { ChatRequestPayload } from '@/components/Markdown/MarkdownEditor/const';
import type {
  ChatHistoryCursorResponse,
  RollbackMessagesResponse,
} from '@/stores/chat';
import type { PublishType } from '@/stores/publish/const';
import { processSSEStream } from '@modules/MessageParser/helper';
import { AppError, ErrorType, isNetworkError } from '@/helpers/error';
import { agentsRequest, onePageRequest } from '@/services/request';
import { getApiBaseUrl } from '@/services/request/helper';
import { fetchSSE } from '@/services/request/sse';
import { PublishStatus } from './const';

export interface ProjectSandboxResource {
  sandboxId: string;
  url: string | null;
  status: 'creating' | 'running' | 'stopped' | 'paused' | 'error' | 'deleted' | 'unknown';
  createdAt: string;
  lastAccessedAt: string | null;
}

export type ProjectSandboxSnapshotResponse = ProjectSandboxResource | null;

export interface ProjectSandboxEnsureResult {
  sandboxId: string;
  url: string;
  status: 'running';
  result: 'reused' | 'resumed' | 'created';
}

export interface ProjectSandboxHeartbeatResult {
  sandboxId: string;
  url: string;
  connection: 'alive' | 'reconnected';
  timestamp: string;
}

// SSE 流式回调接口
export type SSEStreamCallbacks = ProcessSSECallbacks;

// 分页信息接口
export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// 项目列表响应接口
export interface ProjectListResponse {
  list: ProjectDetail[];
  pagination: Pagination;
}

export interface NewProjectGuideResponse {
  guide: string;
  cached: boolean;
  generatedAt: string;
}

export interface FirstDeliveryRecoveryResponse {
  status: ProjectDetail['status'];
  recovered: boolean;
  messageId?: string;
  reason:
    | 'already_completed'
    | 'not_generating'
    | 'missing_completion_evidence'
    | 'generation_stopped'
    | 'delivery_failed'
    | 'recovered'
    | 'busy';
}

export type ProjectListFilter = 'not_generated' | 'generated' | 'not_published' | 'published';
export type ProjectListSortBy = 'lastActiveAt' | 'updatedAt' | 'createdAt';
export type ProjectListSortOrder = 'ASC' | 'DESC';

export interface GetProjectsParams {
  keyword?: string;
  filter?: ProjectListFilter;
  sortBy?: ProjectListSortBy;
  sortOrder?: ProjectListSortOrder;
}

const PROJECT_LIST_PARAM_KEYS = ['keyword', 'filter', 'sortBy', 'sortOrder'];

function isProjectListParams(params?: GetProjectsParams | WegicRequestConfig): params is GetProjectsParams {
  if (!params) {
    return true;
  }

  return Object.keys(params).every(key => PROJECT_LIST_PARAM_KEYS.includes(key));
}

// 模板项目接口
export interface TemplateProject {
  id: string;
  name: string;
  repoPath: string;
  projectPath: string;
  gitlabProjectId: string;
  thumbnailUrl: string;
  previewUrl: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  selected: number;
}

// 模板项目列表响应接口
export interface TemplateProjectListResponse {
  list: TemplateProject[];
  pagination: Pagination;
}

export const PROJECT_PAGE_SIZE = 10;

// 获取项目列表
export function getProjects(
  page?: number,
  pageSize?: number,
  config?: WegicRequestConfig,
): ReturnType<typeof agentsRequest.get<ProjectListResponse>>;
export function getProjects(
  page?: number,
  pageSize?: number,
  params?: GetProjectsParams,
  config?: WegicRequestConfig,
): ReturnType<typeof agentsRequest.get<ProjectListResponse>>;
export async function getProjects(
  page: number = 1,
  pageSize: number = PROJECT_PAGE_SIZE,
  paramsOrConfig: GetProjectsParams | WegicRequestConfig = {},
  config?: WegicRequestConfig,
) {
  const projectListParams = isProjectListParams(paramsOrConfig) ? paramsOrConfig : {};
  const requestConfig = isProjectListParams(paramsOrConfig) ? config : paramsOrConfig;

  return agentsRequest.get<ProjectListResponse>('/projects/v1', {
    ...requestConfig,
    params: {
      page,
      pageSize,
      ...projectListParams,
      ...requestConfig?.params,
    },
  });
}

// 获取最近活跃项目
export async function getRecentProjects(config?: WegicRequestConfig) {
  return agentsRequest.get<ProjectDetail[]>('/projects/v1/recent', config);
}

// 获取模板项目列表
export async function getTemplateProjects(page: number = 1, pageSize: number = 100, config?: WegicRequestConfig) {
  return agentsRequest.get<TemplateProjectListResponse>('/projects/template/v1', {
    ...config,
    params: {
      page,
      pageSize,
      ...config?.params,
    },
  });
}

// 获取新建项目引导词
export function getNewProjectGuide(config?: WegicRequestConfig) {
  return agentsRequest.post<NewProjectGuideResponse>('/projects/v1/new-project-guide', {}, config);
}

// 删除项目
export async function deleteProject(projectId: string, config?: WegicRequestConfig) {
  await agentsRequest.delete(`/projects/v1/${projectId}`, config);
}

// 创建项目
export async function createProject(projectName: string, config?: WegicRequestConfig) {
  return agentsRequest.post<{ projectId: string }>(
    '/projects/v1',
    {
      name: projectName,
    },
    config,
  );
}

// 匿名创建项目
export async function createAnonymousProject(projectName: string, config?: WegicRequestConfig) {
  return agentsRequest.post<{ projectId: string }>(
    '/projects/v1/anonymous',
    {
      name: projectName,
    },
    config,
  );
}

// 登录后把匿名用户的项目归属到当前用户
export async function claimAllAnonymousProjects(anonymousUserId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<{ claimedCount?: number }>('/projects/v1/claim-all', { anonymousUserId }, config);
}

// 获取项目详情
export async function getProjectDetail(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<ProjectDetail>(`/projects/v1/${projectId}`, config);
}

/** 服务重启后，使用服务端持久化证据恢复中断的首次交付收尾。 */
export async function recoverFirstDelivery(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<FirstDeliveryRecoveryResponse>(
    `/projects/v1/${projectId}/first-delivery/recover`,
    {},
    config,
  );
}

// 更新项目详情
export type UpdateProjectDetailSettings = Partial<Pick<ProjectDetail, 'name' | 'description'>>;

export async function updateProjectDetail(
  projectId: string,
  settings: UpdateProjectDetailSettings,
  config?: WegicRequestConfig,
) {
  return agentsRequest.patch<ProjectDetail>(`/projects/v1/${projectId}`, settings, config);
}

// 获取项目设置
export async function getProjectSettings(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<ProjectSettings>(`/projects/v1/${projectId}/settings`, config);
}

// 更新项目设置
export async function updateProjectSettings(
  projectId: string,
  settings: Partial<ProjectSettings>,
  config?: WegicRequestConfig,
) {
  return agentsRequest.patch<ProjectSettings>(`/projects/v1/${projectId}/settings`, settings, config);
}

export interface ProjectSettingsDomain {
  domain: string;
  type: 'FREE' | 'CUSTOM';
  isPrefer: boolean;
  canSetPrefer: boolean;
}

export interface GetProjectDomainSettingsResponse {
  projectId: string;
  domains: ProjectSettingsDomain[];
}

export async function getProjectDomainSettings(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<GetProjectDomainSettingsResponse>(`/projects/v1/${projectId}/settings/domains`, config);
}

export interface SetProjectPreferDomainRequest {
  domain: string;
}

export interface SetProjectPreferDomainResponse {
  success: boolean;
}

export async function setProjectPreferDomain(
  projectId: string,
  request: SetProjectPreferDomainRequest,
  config?: WegicRequestConfig,
) {
  return agentsRequest.put<SetProjectPreferDomainResponse>(
    `/projects/v1/${projectId}/settings/domains/prefer`,
    request,
    config,
  );
}

const DEFAULT_SANDBOX_QUERY_REQUEST_CONFIG: WegicRequestConfig = {
  hideCodeErrorMessage: true,
  hideHTTPErrorMessage: true,
};

const SANDBOX_ENSURE_TIMEOUT_MS = 30000;

export async function getProjectSandbox(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<ProjectSandboxSnapshotResponse>(`/projects/${projectId}/sandbox`, {
    ...DEFAULT_SANDBOX_QUERY_REQUEST_CONFIG,
    ...config,
  });
}

export async function ensureProjectSandbox(projectId: string, sessionId: string, config?: WegicRequestConfig) {
  return agentsRequest.put<ProjectSandboxEnsureResult>(`/projects/${projectId}/sandbox`, { sessionId }, {
    timeout: SANDBOX_ENSURE_TIMEOUT_MS,
    ...config,
  });
}

const DEFAULT_SANDBOX_HEARTBEAT_REQUEST_CONFIG: WegicRequestConfig = {
  hideCodeErrorMessage: true,
  hideHTTPErrorMessage: true,
};

export async function heartbeatProjectSandbox(projectId: string, sandboxId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<ProjectSandboxHeartbeatResult>(
    `/projects/${projectId}/sandbox/${sandboxId}/heartbeat`,
    undefined,
    {
      ...DEFAULT_SANDBOX_HEARTBEAT_REQUEST_CONFIG,
      ...config,
    },
  );
}

// 设置 Wegic 模式
export async function setWegicMode(sandboxId: string, mode: 'dev' | 'preview', config?: WegicRequestConfig) {
  return agentsRequest.put<{ message: string; mode: string }>(`/sandbox/v1/${sandboxId}/wegic-mode`, { mode }, config);
}

export async function getChatHistoryByCursor(
  projectId: string,
  cursor?: string,
  limit: number = 20,
  config?: WegicRequestConfig,
) {
  return agentsRequest.get<ChatHistoryCursorResponse>(`/projects/v1/${projectId}/chat/history/cursor`, {
    ...config,
    params: {
      cursor,
      limit,
      ...config?.params,
    },
  });
}

export async function getRollbackMessages(
  projectId: string,
  messageId: string,
  cursor?: string,
  limit: number = 20,
  config?: WegicRequestConfig,
) {
  return agentsRequest.get<RollbackMessagesResponse>(
    `/projects/v1/${projectId}/chat/messages/${messageId}/rollback-messages`,
    {
      ...config,
      params: {
        cursor,
        limit,
        ...config?.params,
      },
    },
  );
}

// ==================== DOM Selector (修改反馈光标预测) ====================

export interface DomSelectorRequest {
  userMessage: string;
  currentRoute?: string;
  conversationHistory?: { role: string; content: string }[];
  /** 前端从 iframe 提取的 DOM 结构摘要（有此字段时后端跳过沙盒文件读取） */
  domSummary?: string;
  /** 用户语言，用于后端 LLM prompt 语言适配 */
  userLanguage?: string;
}

export interface DomSelectorResponse {
  selectors: string[];
  cursorDialogue: string[];
}

/** DOM Selector API 默认超时 ms（有 domSummary 时后端仅做 LLM 预测，无需沙盒连接） */
const DOM_SELECTOR_TIMEOUT_MS = 12000;

/** 降级兜底数据 */
const DOM_SELECTOR_FALLBACK: DomSelectorResponse = {
  selectors: ['body'],
  cursorDialogue: ['正在分析页面...', '调整中...', '优化样式中...'],
};

/**
 * 调用后端 LLM 预测用户修改目标元素和对话文案
 * 内置 12 秒超时，失败时降级返回 body
 */
export async function getDomSelector(projectId: string, params: DomSelectorRequest): Promise<DomSelectorResponse> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DOM_SELECTOR_TIMEOUT_MS);
    const response = await agentsRequest.post<DomSelectorResponse>(`/projects/v1/${projectId}/dom-selector`, params, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response.data;
  } catch {
    return DOM_SELECTOR_FALLBACK;
  }
}

// ==================== Message Queue ====================

export type QueueItemStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface CreateQueueItemRequest {
  input: ChatRequestPayload['input'];
  allowImmediate?: boolean;
  /** 用户提交消息时 iframe 同一时刻的视窗 DOM 摘要和路由 */
  viewportContext?: {
    domSummary?: string;
    route?: string;
  };
}

export interface AddQueueItemResponse {
  id: string;
  position: number;
  status: QueueItemStatus;
  immediate: boolean;
  messageId: string | null;
  referenceUiGenerationAccepted?: boolean;
}

export interface QueueItemResponse {
  id: string;
  input: ChatRequestPayload['input'];
  status: QueueItemStatus;
  position: number;
  messageId: string | null;
  createdAt: string;
  updatedAt?: string;
  errorMessage?: string | null;
}

export interface QueueListResponse {
  items: QueueItemResponse[];
  total: number;
}

export interface MoveQueueItemRequest {
  afterId?: string;
  beforeId?: string;
}

export interface ConsumeNextQueueItemResponse {
  id: string;
  messageId: string;
  status: 'processing';
}

export async function createQueueItem(projectId: string, payload: CreateQueueItemRequest, config?: WegicRequestConfig) {
  return agentsRequest.post<AddQueueItemResponse>(`/projects/v1/${projectId}/queue`, payload, config);
}

export async function listQueueItems(
  projectId: string,
  status?: QueueItemStatus | QueueItemStatus[],
  config?: WegicRequestConfig,
) {
  const statusParam = Array.isArray(status) ? status.join(',') : status;

  return agentsRequest.get<QueueListResponse>(`/projects/v1/${projectId}/queue`, {
    ...config,
    params: {
      ...(config?.params || {}),
      ...(statusParam ? { status: statusParam } : {}),
    },
  });
}

export async function updateQueueItem(
  projectId: string,
  id: string,
  payload: CreateQueueItemRequest,
  config?: WegicRequestConfig,
) {
  return agentsRequest.put<QueueItemResponse>(`/projects/v1/${projectId}/queue/${id}`, payload, config);
}

export async function deleteQueueItem(projectId: string, id: string, config?: WegicRequestConfig) {
  return agentsRequest.delete<{ success: boolean }>(`/projects/v1/${projectId}/queue/${id}`, config);
}

export async function moveQueueItem(
  projectId: string,
  id: string,
  payload: MoveQueueItemRequest,
  config?: WegicRequestConfig,
) {
  return agentsRequest.patch<QueueItemResponse>(`/projects/v1/${projectId}/queue/${id}/move`, payload, config);
}

export async function consumeNextQueueItem(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<ConsumeNextQueueItemResponse | void>(
    `/projects/v1/${projectId}/queue/consume-next`,
    {},
    config,
  );
}

// ==================== 中断 ====================

export interface InterruptMessageResponse {
  success: boolean;
  messageId: string;
  interruptedAt: number;
  message: string;
}

export interface SubmitAskUserInputPayload {
  answers: Record<string, string | number | boolean | string[]>;
  skipped?: boolean;
}

export interface SubmitAskUserInputResponse {
  success: boolean;
  message: string;
}

export async function submitAskUserInput(
  projectId: string,
  messageId: string,
  interruptId: string,
  payload: SubmitAskUserInputPayload,
  config?: WegicRequestConfig,
) {
  return agentsRequest.post<SubmitAskUserInputResponse>(
    `/projects/v1/${projectId}/chat/messages/${messageId}/user-input`,
    {
      type: 'ask_user',
      interruptId,
      payload,
    },
    config,
  );
}

// 中断正在执行的消息
export async function interruptMessage(
  sessionId: string,
  messageId: string,
  reason?: string,
  config?: WegicRequestConfig,
) {
  return agentsRequest.post<InterruptMessageResponse>(
    `/agent/v1/sessions/${sessionId}/messages/${messageId}/interrupt`,
    { reason },
    config,
  );
}

// 恢复进行中的对话（SSE 流式）
export async function resumeChat(
  projectId: string,
  messageId: string,
  callbacks: SSEStreamCallbacks,
  signal?: AbortSignal,
  lastEventId?: string | number,
): Promise<void> {
  // 第一阶段：fetch 请求（底层工具，带自动重试）
  let response: Response;
  try {
    response = await fetchSSE(`${getApiBaseUrl()}/api/agents/projects/v1/${projectId}/chat/resume`, {
      body: JSON.stringify({
        messageId,
        ...(lastEventId !== undefined ? { lastEventId: Number(lastEventId) || 0 } : {}),
      }),
      signal,
    });
  } catch (error) {
    // fetch 阶段的错误 - 转换为 resume 特定的错误类型
    if (error instanceof AppError) {
      callbacks.onError(error);
    } else if (isNetworkError(error)) {
      callbacks.onError(
        new AppError(ErrorType.NETWORK_ERROR_RESUME_FETCH, error instanceof Error ? error.message : String(error)),
      );
    } else {
      callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
    return;
  }

  // 第二阶段：SSE 流处理（业务逻辑）
  try {
    if (!response.body) {
      throw new AppError(ErrorType.HTTP_ERROR, 'Response body is empty');
    }
    const reader = response.body.getReader();
    await processSSEStream(reader, callbacks);
  } catch (error) {
    // 流处理阶段的错误
    if (error instanceof AppError) {
      callbacks.onError(error);
      return;
    }

    // 检查是否为网络错误（流传输阶段）
    if (isNetworkError(error)) {
      callbacks.onError(
        new AppError(ErrorType.NETWORK_ERROR_RESUME_STREAM, error instanceof Error ? error.message : String(error)),
      );
      return;
    }

    const errorObj = error instanceof Error ? error : new Error(String(error));
    callbacks.onError(errorObj);
  }
}
// ==================== Source 资源管理 ====================

export interface SourceFile {
  id: string;
  projectId: string;
  fileName: string;
  originalName: string;
  fileSize: string | number; // 上传返回number，查询返回string
  mimeType: string;
  fileType: string;
  fileUrl: string;
  fileId: string;
  uploadDir: string | null;
  createdAt: string;
}

/**
 * 上传文件到项目
 */
export async function uploadSourceFile(projectId: string, file: File): Promise<SourceFile> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await agentsRequest.post<{ source: SourceFile }>(
    `/projects/v1/${projectId}/sources/upload`,
    formData,
  );

  if (!response.data?.source) {
    throw new Error('文件上传失败: 响应格式错误');
  }

  return response.data.source;
}

/**
 * 获取项目的所有文件
 */
export async function getSourceFiles(projectId: string): Promise<SourceFile[]> {
  const result = await agentsRequest.get<{ sources: SourceFile[]; total: number }>(`/projects/v1/${projectId}/sources`);
  return result.data.sources || [];
}

/**
 * 删除文件
 */
export async function deleteSourceFile(projectId: string, sourceId: string): Promise<void> {
  await agentsRequest.delete(`/projects/v1/${projectId}/sources/${sourceId}`);
}

/**
 * 根据文件路径获取文件内容
 */
export interface FileContent {
  path: string;
  content: string;
}

export async function getFileContent(
  sandboxId: string,
  filePath: string,
  config?: WegicRequestConfig,
): Promise<FileContent> {
  const response = await agentsRequest.get<FileContent>(`/sandbox/v1/${sandboxId}/file`, {
    ...config,
    params: {
      path: filePath,
    },
  });
  return response.data;
}

/**
 * 批量更新文件内容
 */
export interface FileChange {
  path: string;
  content: string;
}

export interface EditCodeRequest {
  changes: FileChange[];
}

export async function editCode(
  sandboxId: string,
  request: EditCodeRequest,
  config?: WegicRequestConfig,
): Promise<void> {
  await agentsRequest.post(`/sandbox/v1/${sandboxId}/edit-code`, request, config);
}

/**
 * 重命名文件
 */
export async function renameSourceFile(projectId: string, sourceId: string, newFileName: string): Promise<void> {
  await agentsRequest.patch(`/projects/v1/${projectId}/sources/${sourceId}/name`, {
    fileName: newFileName,
  });
}

// ==================== Publish 发布管理 ====================

export * from './cloudDatabase';

/**
 * 更新发布信息
 */
export interface UpdatePublishDto {
  subDomain?: string;
  status?: PublishStatus | string;
  type?: PublishType;
  path?: string;
}

export interface PublishResponse {
  readyTag?: string;
  runningTag?: string;
  commitSha: string;
  publishUrl?: string;
  firstPublish?: boolean;
  errorMessage?: string;
}

export interface PublishResultInfo extends PublishResponse {
  duration: number;
}

export async function publishProject(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<PublishResponse>(`/projects/v1/${projectId}/publish`, undefined, config);
}

/**
 * 更新项目发布信息
 */
export async function updatePublishInfo(projectId: string, updateDto: UpdatePublishDto, config?: WegicRequestConfig) {
  return agentsRequest.patch<PublishInfo>(`/projects/v1/${projectId}/publish`, updateDto, config);
}

/**
 * 取消发布项目
 */
export async function unpublishProject(projectId: string, config?: WegicRequestConfig) {
  return updatePublishInfo(projectId, { status: PublishStatus.NotPublished }, config);
}

/**
 * 获取项目发布信息
 */
export interface PublishInfo {
  id: string;
  projectId: string;
  status: PublishStatus;
  type: PublishType;
  subDomain: string | null;
  customDomain: string | null;
  customDomainCname: string | null;
  customDomainIp: string | null;
  customDomainRoot: boolean;
  customDomainPrefix: string;
  firstPublishAt: string | null;
  lastPublishAt: string | null;
  expirePublishAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetPublishInfoResponse {
  projectId: string;
  domain: string;
  publishInfo: PublishInfo | null;
}

export async function getPublishInfo(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<GetPublishInfoResponse>(`/projects/v1/${projectId}/publish`, config);
}

/**
 * 提交自定义域名
 */
export interface SubmitCustomDomainRequest {
  customDomain: string;
  subDomain?: string;
  customDomainCname?: string;
  customDomainIp?: string;
  note?: string;
}

export interface SubmitCustomDomainResponse {
  cname: string;
  ip?: string;
  rootDomain: boolean;
  customDomainPrefix: string;
}

export async function submitCustomDomain(
  projectId: string,
  request: SubmitCustomDomainRequest,
  config?: WegicRequestConfig,
) {
  return agentsRequest.post<SubmitCustomDomainResponse>(
    `/projects/v1/${projectId}/publish/domain/submit`,
    request,
    config,
  );
}

/**
 * 检查域名 DNS 状态
 */
export interface CheckDomainRequest {
  customDomain?: string;
  subDomain?: string;
}

export interface CheckDomainResponse {
  domain?: string;
  status?: string;
  isAvailable: boolean;
  context?: {
    CnameRecords?: string[] | null;
    ARecords?: string[] | null;
    ttl?: number;
    error?: string;
  };
}

export async function checkCustomDomain(projectId: string, request: CheckDomainRequest, config?: WegicRequestConfig) {
  return agentsRequest.post<CheckDomainResponse>(`/projects/v1/${projectId}/publish/domain/check`, request, config);
}

/**
 * 删除自定义域名
 */
export interface DeleteCustomDomainResponse {
  success: boolean;
  message: string;
}

export async function deleteCustomDomain(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.delete<DeleteCustomDomainResponse>(`/projects/v1/${projectId}/publish/domain`, config);
}

// ==================== V1 项目管理 ====================

/**
 * V1 项目列表响应接口
 */
export interface V1ProjectListResponse {
  pageData: {
    pageNum: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
  list: Array<{
    id: string;
    userId: string;
    status: number;
    assistantSite?: {
      title?: string;
      subDomain?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  }>;
}

export interface CommitTimelineItem {
  type: 'commit';
  id: string;
  projectId: string;
  commitSha: string;
  branch: string;
  tag: string | null;
  gitlabProjectId: number | null;
  sandboxId: string | null;
  userId: string | null;
  agentName: string | null;
  sessionId: string | null;
  messageId: string | null;
  userInput: string | null;
  commitMessage: string | null;
  routes: string[] | null;
  syncStatus: 'synced' | 'pending_push' | 'gitlab_unreachable';
  isReady: boolean;
  isRunning: boolean;
  isVisible: boolean;
  isFavorite: 0 | 1;
  createdAt: string;
  previewUrl: string;
}

export interface RollbackTimelineItem {
  type: 'operation';
  id: string;
  projectId: string;
  operationType: 'rollback';
  fromCommitSha: string | null;
  targetCommitSha: string;
  targetCommitId: string | null;
  targetChatMessageId: string | null;
  operationRequestId: string | null;
  sandboxId: string | null;
  userId: string | null;
  operationMessage: string | null;
  userInput: string | null;
  isFavorite: null;
  createdAt: string;
  previewUrl: string;
}

export type VersionTimelineItem = CommitTimelineItem | RollbackTimelineItem;

export interface VersionTimelineData {
  list: VersionTimelineItem[];
  pagination: {
    pageSize: number;
    nextCursor: string | null;
    hasNextPage: boolean;
  };
}

// 切换版本
export interface ResRollbackProjectVersion {
  commitSha: string;
  operationId: string;
}

/**
 * 获取 V1 项目列表
 */
export async function getV1Projects(pageNum: number = 1, pageSize: number = 10, config?: WegicRequestConfig) {
  return onePageRequest.get<V1ProjectListResponse>('/assistantThread', {
    ...config,
    params: {
      pageNum,
      pageSize,
      ...config?.params,
    },
  });
}

/**
 * 下载项目源代码
 */
export async function downloadProjectSource(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.post<DownloadProjectSourceResponse>(`/projects/v1/${projectId}/download-source`, {}, config);
}
export async function getVersionTimeline(
  projectId: string,
  params: { cursor?: string; limit?: number } = {},
  config?: WegicRequestConfig,
): Promise<VersionTimelineData> {
  const { cursor, limit = 20 } = params;
  const response = await agentsRequest.get<VersionTimelineData>(
    `/projects/v1/${projectId}/versions/timeline`,
    {
      ...config,
      params: {
        limit,
        ...(cursor ? { cursor } : {}),
        ...config?.params,
      },
    },
  );

  return response.data;
}

export type PrePublishBuildStatus = 'not_started' | 'queued' | 'running' | 'succeeded' | 'failed' | 'timeout' | 'cancelled';

export interface PrePublishBuildStatusData {
  status: PrePublishBuildStatus;
  commitSha: string;
  buildId?: string;
  attemptNo?: number;
  previewUrl: string | null;
  errorStage?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

export async function getPrePublishBuildStatus(
  projectId: string,
  commitSha: string,
  config?: WegicRequestConfig,
): Promise<PrePublishBuildStatusData> {
  const response = await agentsRequest.get<PrePublishBuildStatusData>(
    `/projects/v1/${projectId}/pre-publish/status`,
    {
      ...config,
      params: { commitSha, ...config?.params },
    },
  );
  return response.data;
}

export interface UpdateVersionFavoriteData {
  commitId: string;
  isFavorite: boolean;
}

export async function updateVersionFavorite(
  projectId: string,
  commitId: string,
  isFavorite: boolean,
  config?: WegicRequestConfig,
): Promise<UpdateVersionFavoriteData> {
  const response = await agentsRequest.patch<UpdateVersionFavoriteData>(
    `/projects/v1/${projectId}/versions/${commitId}/favorite`,
    { isFavorite },
    config,
  );

  return response.data;
}

/**
 * 切换版本
 */
export async function rollbackProjectVersion(
  projectId: string,
  versionId: string,
  operationKey: string,
  config?: WegicRequestConfig,
) {
  return agentsRequest.post<ResRollbackProjectVersion>(
    `/projects/v1/${projectId}/versions/${versionId}/rollback`,
    undefined,
    {
      ...config,
      headers: {
        ...config?.headers,
        'Idempotency-Key': operationKey,
      },
    },
  );
}

export { PublishStatus } from './const';
export * from './toolApproval';
export * from './trash';

/**
 * 任务项接口
 */
export interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed';
  actor: 'ai' | 'user';
  estimatedTime: string;
  createdAt: number;
  priority?: string;
}

/**
 * 获取项目任务列表
 */
export async function getProjectTasks(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<{ todos: Task[] }>(`/projects/v1/todos/${projectId}`, config);
}

// ==================== 项目信息 ====================

/**
 * 项目信息接口响应
 */
export interface ProjectInfoResponse {
  summary?: string;
  website_info?: {
    goals?: string[] | string;
    industry?: string;
    language?: string;
    overview?: string;
    website_type?: string;
    target_audience?: string[] | string;
    functional_requirements?: string[] | string;
  };
  design_preferences?: string[] | string;
  content_preferences?: string[] | string;
  behavior_preferences?: string[] | string;
  brand_guidelines?: string[] | string;
}

/**
 * 获取项目信息
 */
export async function getProjectInfo(projectId: string, config?: WegicRequestConfig) {
  return agentsRequest.get<ProjectInfoResponse>(`/projects/v1/${projectId}/info`, config);
}
